import { isImageFileName } from "@/lib/utils/file";

export type UnifiedMediaKind =
  | "image"
  | "video"
  | "audio"
  | "pdf"
  | "spreadsheet"
  | "file";

const VIDEO_EXTENSIONS = new Set(["mp4", "webm", "ogg", "mov", "avi"]);
const AUDIO_EXTENSIONS = new Set(["mp3", "wav", "m4a", "aac", "ogg", "flac"]);
const SPREADSHEET_EXTENSIONS = new Set(["csv", "xlsx", "xls"]);

export const getFileExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf(".");
  if (lastDot === -1) return "";
  return fileName.substring(lastDot + 1).toLowerCase();
};

export const formatFileSize = (size: number | string): string => {
  const bytes = typeof size === "string" ? Number.parseFloat(size) : size;
  if (!Number.isFinite(bytes) || bytes < 0) return "Unknown size";
  if (bytes === 0) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );

  if (unitIndex === 0) {
    return `${Math.round(bytes)} ${units[unitIndex]}`;
  }

  const formatted = bytes / 1024 ** unitIndex;
  return `${Number.parseFloat(formatted.toFixed(2))} ${units[unitIndex]}`;
};

export const inferMediaKind = ({
  fileName,
  mimeType,
}: {
  fileName: string;
  mimeType?: string | null;
}): UnifiedMediaKind => {
  const extension = getFileExtension(fileName);
  const mime = mimeType ?? "";

  if (mime.startsWith("image/") || isImageFileName(fileName)) {
    return "image";
  }
  if (mime.startsWith("video/") || VIDEO_EXTENSIONS.has(extension)) {
    return "video";
  }
  if (mime.startsWith("audio/") || AUDIO_EXTENSIONS.has(extension)) {
    return "audio";
  }
  if (mime === "application/pdf" || extension === "pdf") {
    return "pdf";
  }
  if (
    SPREADSHEET_EXTENSIONS.has(extension) ||
    mime.includes("spreadsheet") ||
    mime.includes("csv")
  ) {
    return "spreadsheet";
  }

  return "file";
};

export const triggerBrowserDownload = ({
  href,
  fileName,
}: {
  href: string;
  fileName: string;
}) => {
  const link = document.createElement("a");
  link.href = href;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadBlobAsFile = ({
  blob,
  fileName,
}: {
  blob: Blob;
  fileName: string;
}) => {
  const objectUrl = URL.createObjectURL(blob);
  triggerBrowserDownload({ href: objectUrl, fileName });

  window.setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
  }, 1000);
};
